import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_WEB_ACTIVITY } from '../../../lib/graphql/queries/contacts';
import type { GQLWebActivityItem } from './useContact';

const PAGE_SIZE = 50;

interface GetContactWebActivityData {
  contact: {
    contactId: string;
    webActivity: {
      items: GQLWebActivityItem[];
      total: number;
      hasMore: boolean;
    };
  } | null;
}

export const useContactWebActivityGQL = (
  contactId: string,
  eventTypes?: string[]
) => {
  const { organization } = useOrganizationStore();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useQuery<GetContactWebActivityData>(GET_CONTACT_WEB_ACTIVITY, {
    variables: {
      organizationId: organization?.id ?? '',
      contactId,
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
      ...(eventTypes?.length && { eventTypes }),
      sortOrder: 'DESC',
    },
    skip: !organization?.id || !contactId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const items = data?.contact?.webActivity?.items ?? [];
  const total = data?.contact?.webActivity?.total ?? 0;
  const hasMore = data?.contact?.webActivity?.hasMore ?? false;

  const handleNextPage = useCallback(() => setCurrentPage((p) => p + 1), []);
  const handlePrevPage = useCallback(() => setCurrentPage((p) => Math.max(0, p - 1)), []);
  const goToPage = useCallback((page: number) => setCurrentPage(Math.max(0, page)), []);
  const resetPage = useCallback(() => setCurrentPage(0), []);

  return {
    items,
    total,
    hasMore,
    loading,
    error,
    currentPage,
    pageSize: PAGE_SIZE,
    hasNext: hasMore,
    handleNextPage,
    handlePrevPage,
    goToPage,
    resetPage,
  };
};
